import { useBluelagoon, Bluelagoon } from "@bluelagoon";
import { toast } from "sonner";

// Trigger type mappings for skills
export const SKILL_TRIGGER_MAPPINGS = {
    "Receive Email": {
        type: "EMAIL",
        displayName: "Email Trigger",
        icon: "📧",
        description: "Receive emails to trigger workflows",
    },
    "Receive WhatsApp": {
        type: "WHATSAPP",
        displayName: "WhatsApp Trigger",
        icon: "💬",
        description: "Receive WhatsApp messages to trigger workflows",
    },
    Webhook: {
        type: "WEBHOOK",
        displayName: "Webhook Trigger",
        icon: "🌐",
        description: "Receive webhook data to trigger workflows",
    },
    Schedule: {
        type: "TIME",
        displayName: "Schedule Trigger",
        icon: "⏰",
        description: "Run workflows on a schedule",
    },
};

// Trigger generation service
export class TriggerService {
    constructor(updateTrigger, deleteWorkflowTrigger) {
        this.updateTrigger = updateTrigger;
        this.deleteWorkflowTrigger = deleteWorkflowTrigger;
    }

    // Generate email trigger (adapted from EmailEndpoint.jsx)
    async generateEmailTrigger(options = {}) {
        try {
            const response = await this.updateTrigger({
                workflowId: options.workflowId || null,
                type: "EMAIL",
                config: {},
                chatMode: !options.workflowId, // Flag to indicate this is for chat mode
            });

            if (response.status === 200) {
                const emailTrigger = response.trigger;
                const emailConfig = emailTrigger.config.find(
                    (c) => c.key === "emailAddress"
                );

                return {
                    success: true,
                    triggerId: emailTrigger.id, // Include trigger ID for data mapping
                    triggerType: "EMAIL",
                    value: emailConfig?.value || "",
                    displayValue: emailConfig?.value || "",
                    copyText: emailConfig?.value || "",
                    instructions:
                        "Send emails to this address to trigger your workflow",
                };
            } else {
                throw new Error("Failed to generate email endpoint");
            }
        } catch (error) {
            console.error("Error generating email trigger:", error);
            return {
                success: false,
                error: error.message,
            };
        }
    }

    // Generate webhook trigger (adapted from WebhookEndpoint.jsx)
    async generateWebhookTrigger(options = {}) {
        try {
            const response = await this.updateTrigger({
                workflowId: options.workflowId || null,
                type: "WEBHOOK",
                config: {},
                chatMode: !options.workflowId,
            });

            if (response.status === 200) {
                const webhookTrigger = response.trigger;
                const webhookUrl = `${Bluelagoon.apiUrl || "https://api.agentui.ai"}/webhook/${webhookTrigger.publicKey}`;

                return {
                    success: true,
                    triggerId: webhookTrigger.id, // Include trigger ID for data mapping
                    triggerType: "WEBHOOK",
                    value: webhookUrl,
                    displayValue: webhookUrl,
                    copyText: webhookUrl,
                    instructions:
                        "Use this URL to trigger workflows externally via HTTP POST",
                };
            } else {
                throw new Error("Failed to generate webhook endpoint");
            }
        } catch (error) {
            console.error("Error generating webhook trigger:", error);
            return {
                success: false,
                error: error.message,
            };
        }
    }

    // Generate WhatsApp trigger (adapted from WhatsappTriggerConfig.jsx)
    async generateWhatsAppTrigger(
        selectedNumberId,
        allowedPhones = [],
        options = {}
    ) {
        try {
            const response = await this.updateTrigger({
                workflowId: options.workflowId || null,
                type: "WHATSAPP",
                whatsappNumberId: selectedNumberId,
                config: {
                    allowedPhones: allowedPhones.map((phone) =>
                        phone.replace(/[\s\-+]/g, "")
                    ),
                },
                chatMode: !options.workflowId,
            });

            if (response.status === 200 || response.status === 201) {
                const trigger = response.trigger || response.data;
                const whatsappNumber = trigger?.whatsappTrigger?.whatsappNumber;
                const displayNumber =
                    whatsappNumber?.number || "WhatsApp Number";

                return {
                    success: true,
                    triggerId: trigger.id, // Include trigger ID for data mapping
                    triggerType: "WHATSAPP",
                    value: {
                        numberId: selectedNumberId,
                        number: displayNumber,
                        allowedPhones: allowedPhones,
                    },
                    displayValue: `${displayNumber} (${allowedPhones.length} allowed contacts)`,
                    copyText: displayNumber,
                    instructions:
                        "Send WhatsApp messages to this number to trigger workflows",
                };
            } else {
                throw new Error("Failed to generate WhatsApp trigger");
            }
        } catch (error) {
            console.error("Error generating WhatsApp trigger:", error);
            return {
                success: false,
                error: error.message,
            };
        }
    }

    // Generate time trigger (schedule)
    async generateTimeTrigger(options = {}) {
        try {
            // Validate and format time
            let timeOfDay = options.timeOfDay || "09:00";

            // Ensure time is in HH:MM format
            if (!/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeOfDay)) {
                throw new Error(
                    `Invalid time format: ${timeOfDay}. Expected format: HH:MM`
                );
            }

            // Ensure time is in 24-hour format with leading zeros
            const [hours, minutes] = timeOfDay.split(":");
            const formattedHours = hours.padStart(2, "0");
            const formattedMinutes = minutes.padStart(2, "0");
            timeOfDay = `${formattedHours}:${formattedMinutes}`;

            const payload = {
                workflowId: options.workflowId || null,
                type: "TIME",
                config: {
                    timeOfDay,
                    timezone: options.timezone || "UTC",
                    weekdays: options.weekdays || [],
                    daysOfMonth: options.daysOfMonth || [],
                    cron: options.cron || "",
                },
                chatMode: !options.workflowId,
            };

            const response = await this.updateTrigger(payload);

            if (response.status === 200) {
                const timeTrigger = response.trigger;

                // Look for time configuration directly in the config array
                const timeOfDayConfig = timeTrigger.config.find(
                    (c) => c.key === "timeOfDay"
                );
                const timezoneConfig = timeTrigger.config.find(
                    (c) => c.key === "timezone"
                );
                const weekdaysConfig = timeTrigger.config.find(
                    (c) => c.key === "weekdays"
                );
                const daysOfMonthConfig = timeTrigger.config.find(
                    (c) => c.key === "daysOfMonth"
                );

                return {
                    success: true,
                    triggerId: timeTrigger.id,
                    triggerType: "TIME",
                    value: {
                        timeOfDay: timeOfDayConfig?.value || timeOfDay,
                        timezone:
                            timezoneConfig?.value || options.timezone || "UTC",
                        weekdays: weekdaysConfig?.value || [],
                        daysOfMonth: daysOfMonthConfig?.value || [],
                    },
                    displayValue: `Scheduled at ${
                        timeOfDayConfig?.value || timeOfDay
                    } ${timezoneConfig?.value || options.timezone || "UTC"}`,
                    copyText: `${timeOfDayConfig?.value || timeOfDay} ${
                        timezoneConfig?.value || options.timezone || "UTC"
                    }`,
                    instructions:
                        "Workflow will run automatically on the configured schedule",
                };
            } else {
                throw new Error("Failed to generate time trigger");
            }
        } catch (error) {
            console.error("Error generating time trigger:", error);
            return {
                success: false,
                error: error.message,
            };
        }
    }

    // Get available WhatsApp numbers (used for WhatsApp trigger setup)
    async getAvailableWhatsAppNumbers() {
        try {
            // We need to use a separate API call for fetching WhatsApp numbers
            // This should be implemented with the correct bluelagoon method
            console.warn(
                "WhatsApp numbers fetching needs proper implementation"
            );
            return [];
        } catch (error) {
            console.error("Error fetching WhatsApp numbers:", error);
            return [];
        }
    }

    async deleteTrigger(triggerId, workflowId) {
        try {
            const response = await this.deleteWorkflowTrigger({
                triggerId,
                workflowId,
            });
        } catch (error) {
            console.error("Error deleting trigger:", error);
            return {
                success: false,
                error: error.message,
            };
        }
    }
}

// React hook for using trigger service
export const useTriggerService = () => {
    const { call: updateTrigger } = useBluelagoon({
        method: "updateWorkflowTrigger",
    });

    const { call: deleteWorkflowTrigger } = useBluelagoon({
        method: "deleteWorkflowTrigger",
    });

    // Separate API call for fetching WhatsApp numbers
    const { call: fetchWhatsAppNumbers } = useBluelagoon({
        method: "getAvailableWhatsappNumbers",
    });

    const triggerService = new TriggerService(
        updateTrigger,
        deleteWorkflowTrigger
    );

    const generateTriggerForSkill = async (skill, options = {}) => {
        const triggerMapping = SKILL_TRIGGER_MAPPINGS[skill.name];

        if (!triggerMapping) {
            toast.error(`${skill.name} does not have trigger support`);
            return {
                success: false,
                error: "Skill not supported for triggers",
            };
        }

        let result;

        try {
            switch (triggerMapping.type) {
                case "EMAIL":
                    result = await triggerService.generateEmailTrigger(options);
                    break;
                case "WHATSAPP":
                    result = await triggerService.generateWhatsAppTrigger(
                        options.selectedNumberId,
                        options.allowedPhones || [],
                        options
                    );
                    break;
                case "WEBHOOK":
                    result = await triggerService.generateWebhookTrigger(
                        options
                    );
                    break;
                case "TIME":
                    result = await triggerService.generateTimeTrigger(options);
                    break;
                default:
                    throw new Error(
                        `Unsupported trigger type: ${triggerMapping.type}`
                    );
            }

            if (result.success) {
                toast.success(
                    `${triggerMapping.displayName} generated successfully!`
                );
            } else {
                toast.error(
                    `Failed to generate ${triggerMapping.displayName}: ${result.error}`
                );
            }

            return result;
        } catch (error) {
            console.error("Error generating trigger:", error);
            toast.error(`Failed to generate trigger: ${error.message}`);
            return { success: false, error: error.message };
        }
    };

    return {
        generateTriggerForSkill,
        triggerService,
        SKILL_TRIGGER_MAPPINGS,
        deleteTrigger: triggerService.deleteTrigger.bind(triggerService),
    };
};
